import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Play, Sparkles, Moon, Video as VideoIcon, Headphones, BookOpen,
  ChevronLeft, ChevronRight, Heart, Waves, Wind, Compass, ArrowRight, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import useToday from '../hooks/useToday';
import useEntitlement from '../hooks/useEntitlement';
import { useAudio } from '../context/AudioContext';
import { useAuth } from '../context/AuthContext';
import MoodCheckInCard from './MoodCheckInCard';
import PaywallModal from './PaywallModal';
import VideoModal from './VideoModal';
import { trackEvent } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// FNV-ish 32-bit string hash for deterministic non-manifest index fallback.
const hashCode = (s) => {
  let h = 0;
  const str = String(s || '');
  for (let i = 0; i < str.length; i += 1) { h = ((h << 5) - h) + str.charCodeAt(i); h |= 0; }
  return h;
};

const toastVoiceThrottled   = () => toast.info("A moment — Rowdy's voice is catching its breath.");
const toastVoiceCredits     = () => toast.error("Rowdy's voice is topping up. Please try again shortly.");
const toastVoiceUnavailable = () => toast.error("Rowdy's voice is unavailable right now.");

// --------------------------------------------------------------------------- //
// Small display helpers
// --------------------------------------------------------------------------- //

const TypeIcon = ({ type, className = 'w-4 h-4' }) => {
  switch (type) {
    case 'soundscape':      return <Waves className={className} />;
    case 'sleep_meditation':
    case 'sleep_story':     return <Moon className={className} />;
    case 'breathing_audio': return <Wind className={className} />;
    case 'video':           return <VideoIcon className={className} />;
    case 'affirmation':     return <Sparkles className={className} />;
    case 'rowdy_message':   return <Heart className={className} />;
    default:                return <Headphones className={className} />;
  }
};

// Resized WebP variant for cover art served from our media endpoint.
const thumb = (u, w = 480) => (u && u.includes('/api/media/') && !u.includes('?') ? `${u}?w=${w}` : u);

const fmtDuration = (sec) => {
  if (!sec) return null;
  if (sec < 60) return `${sec}s`;
  return `${Math.round(sec / 60)} min`;
};

// --------------------------------------------------------------------------- //
// Reusable rail (horizontally scrolling card list)
// --------------------------------------------------------------------------- //

const Rail = ({ title, subtitle, items, onPick, onPlay, testid, cardVariant = 'default', anchorId }) => {
  const scrollRef = React.useRef(null);
  const sectionRef = React.useRef(null);
  const [canArrows, setCanArrows] = React.useState(false);

  // If this rail is the current URL hash target, smooth-scroll it into view
  // once its items have rendered. Retries generously to survive async data
  // loads AND lazy-rendered rails that only mount when the user has scrolled
  // past the top of a tall Today page.
  React.useEffect(() => {
    if (!anchorId) return;
    if (!items || items.length === 0) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash !== `#${anchorId}`) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 60;   // ~15s worth of retries at 250ms
    const RETRY_MS    = 250;
    const tryScroll = () => {
      if (cancelled) return;
      attempts += 1;
      const el = sectionRef.current || document.getElementById(anchorId);
      if (el) {
        // Nudge the browser first — some viewport heights don't render the
        // full page tree until we've scrolled at least once.
        try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* older browsers */ }
        return;
      }
      if (attempts < MAX_ATTEMPTS) setTimeout(tryScroll, RETRY_MS);
    };
    const t = setTimeout(tryScroll, 60);
    return () => { cancelled = true; clearTimeout(t); };
  }, [anchorId, items]);

  const recomputeArrows = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Only show arrows when content actually overflows (i.e. multi-card rail on desktop or wide mobile).
    setCanArrows(el.scrollWidth > el.clientWidth + 4);
  }, []);

  React.useEffect(() => {
    recomputeArrows();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(recomputeArrows);
    ro.observe(el);
    window.addEventListener('resize', recomputeArrows);
    return () => { ro.disconnect(); window.removeEventListener('resize', recomputeArrows); };
  }, [items, recomputeArrows]);

  const scrollByArrow = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardW = 190 + 12; // card width + gap
    const step = cardW * Math.max(1, Math.floor(el.clientWidth / cardW));
    const max = el.scrollWidth - el.clientWidth;
    let next = el.scrollLeft + dir * step;
    // Infinite wrap: past the last card → back to start; before start → to end.
    if (dir > 0 && el.scrollLeft >= max - 4) next = 0;
    else if (dir < 0 && el.scrollLeft <= 4) next = max;
    el.scrollTo({ left: next, behavior: 'smooth' });
  };

  if (!items || items.length === 0) return null;

  return (
    <section ref={sectionRef} id={anchorId} className="mt-6 relative group scroll-mt-24" data-testid={testid}>
      <div className="flex items-baseline justify-between px-1 mb-2">
        <div>
          <h2
            className="text-lg leading-tight text-stone-800"
            style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }}
          >
            {title}
          </h2>
          {subtitle && <p className="text-[12px] text-stone-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>

      {canArrows && (
        <>
          <button
            type="button"
            aria-label="Previous"
            data-testid={`${testid}-prev`}
            onClick={() => scrollByArrow(-1)}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full items-center justify-center bg-[#FFFCF6]/95 border border-stone-200 shadow-md hover:bg-white transition-opacity opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft className="w-5 h-5 text-stone-700" />
          </button>
          <button
            type="button"
            aria-label="Next"
            data-testid={`${testid}-next`}
            onClick={() => scrollByArrow(1)}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full items-center justify-center bg-[#FFFCF6]/95 border border-stone-200 shadow-md hover:bg-white transition-opacity opacity-0 group-hover:opacity-100"
          >
            <ChevronRight className="w-5 h-5 text-stone-700" />
          </button>
        </>
      )}

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: 'none' }}
      >
        {items.map((it) => (
          <div
            key={it.id}
            className="snap-start shrink-0 w-[190px] relative"
            style={{ minHeight: cardVariant === 'wide' ? 200 : 220 }}
          >
            <button
              type="button"
              onClick={() => onPick(it)}
              data-testid={`${testid}-card-${it.slug || it.id}`}
              className="w-full block rounded-2xl overflow-hidden text-left active:scale-[0.98] transition-transform"
            >
              <div className="relative w-full h-[130px]">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={(it.coverImageUrl || it.posterImageUrl)
                    ? { backgroundImage: `url("${thumb(it.coverImageUrl || it.posterImageUrl)}")` }
                    : { background: 'linear-gradient(135deg, #4a7c65 0%, #173F35 100%)' }}
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(180deg, rgba(13,43,36,0.05) 0%, rgba(13,43,36,0.55) 100%)' }}
                />
                <span
                  className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider"
                  style={{ background: 'rgba(255,252,246,0.85)', color: '#173F35' }}
                >
                  <TypeIcon type={it.type} className="w-3 h-3" />
                  {it.type?.replace('_', ' ')}
                </span>
                {(it.accessLevel || 'free').toLowerCase() === 'premium' && (
                  <span
                    className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider shadow-sm"
                    style={{ background: '#C79A4A', color: '#0D2B24' }}
                    data-testid={`premium-badge-${it.slug || it.id}`}
                    aria-label="Premium"
                  >
                    <Lock className="w-2.5 h-2.5" /> Premium
                  </span>
                )}
                {/* Spacer where the play button will sit — kept in the layout
                    so the card body's height matches the previous design. */}
                <span className="absolute bottom-2 right-2 w-9 h-9" aria-hidden />
              </div>
              <div className="p-3 bg-[#FFFCF6] border border-stone-100 border-t-0 rounded-b-2xl">
                <p className="text-[13px] font-semibold text-stone-800 leading-snug line-clamp-2"
                   style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }}>
                  {it.title}
                </p>
                <div className="mt-1 flex items-center justify-between text-[10px] text-stone-500">
                  <span className="truncate pr-2">
                    {it.category || it.creator || ''}
                  </span>
                  {fmtDuration(it.durationSeconds) && <span>{fmtDuration(it.durationSeconds)}</span>}
                </div>
                {it.reasonLabel && (
                  <p className="text-[10px] mt-1 truncate" style={{ color: '#C79A4A' }}>
                    {it.reasonLabel}
                  </p>
                )}
              </div>
            </button>
            {/* Sibling — never nested inside the parent <button> so it captures
                the click on its own. Absolutely positioned over the artwork
                exactly where the previous play badge sat. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); (onPlay || onPick)(it); }}
              aria-label={`Play ${it.title}`}
              data-testid={`${testid}-play-${it.slug || it.id}`}
              className="absolute top-[calc(130px-2.75rem)] right-2 w-9 h-9 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              style={{ background: '#C79A4A', color: '#0D2B24' }}
            >
              <Play className="w-4 h-4 ml-0.5" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

// --------------------------------------------------------------------------- //
// Featured single-card (For This Moment / Sleep / Moment with Rowdy)
// --------------------------------------------------------------------------- //

const FeatureCard = ({ item, onPick, onPlay, eyebrow, testid, tone = 'green' }) => {
  if (!item) return null;
  const bgArt = thumb(item.coverImageUrl || item.posterImageUrl, 960);
  const gradient = tone === 'night'
    ? 'linear-gradient(160deg, #1e3a5f 0%, #0D2B24 100%)'
    : tone === 'gold'
      ? 'linear-gradient(160deg, #C79A4A 0%, #6b4c1a 100%)'
      : 'linear-gradient(160deg, #4a7c65 0%, #173F35 100%)';
  return (
    <div className="mt-6 relative">
      <button
        type="button"
        onClick={() => onPick(item)}
        data-testid={testid}
        className="w-full text-left rounded-3xl overflow-hidden relative active:scale-[0.99] transition-transform shadow-lg block"
        style={{ minHeight: 180 }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: bgArt ? `url(${bgArt})` : undefined, background: bgArt ? undefined : gradient }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,43,36,0.15) 0%, rgba(13,43,36,0.82) 100%)' }} />
        <div className="relative p-5 flex flex-col justify-end min-h-[180px]" style={{ color: '#F6F1E7' }}>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#C79A4A' }}>
            {eyebrow}
          </p>
          <p
            className="text-2xl leading-tight mt-1"
            style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }}
          >
            {item.title}
          </p>
          {item.shortDescription && (
            <p className="text-[13px] mt-1 line-clamp-2" style={{ color: 'rgba(246,241,231,0.75)' }}>
              {item.shortDescription}
            </p>
          )}
          <div className="mt-3 flex items-center justify-between">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold"
              style={{ background: 'rgba(199,154,74,0.22)', color: '#C79A4A', border: '1px solid rgba(199,154,74,0.4)' }}
            >
              <TypeIcon type={item.type} className="w-3 h-3" />
              {item.reasonLabel || item.type?.replace('_', ' ')}
            </span>
            {/* Layout placeholder — the real play button is a sibling below. */}
            <span className="w-11 h-11" aria-hidden />
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); (onPlay || onPick)(item); }}
        aria-label={`Play ${item.title}`}
        data-testid={`${testid}-play`}
        className="absolute bottom-5 right-5 w-11 h-11 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
        style={{ background: '#C79A4A', color: '#0D2B24' }}
      >
        <Play className="w-5 h-5 ml-0.5" />
      </button>
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Root — TodayPersonalized
// --------------------------------------------------------------------------- //

const TodayPersonalized = ({ coachSlot = null }) => {
  const { user } = useAuth();
  const { play } = useAudio();
  const navigate = useNavigate();
  const { data, loading, refresh } = useToday([user?.id]);
  const { isPremium } = useEntitlement();
  const [paywall, setPaywall] = useState({ open: false, title: null });
  const [videoModal, setVideoModal] = useState({ open: false, item: null });

  const isLocked = (item) => {
    if (!item) return false;
    return (item.accessLevel || 'free').toLowerCase() === 'premium' && !isPremium;
  };

  // Manifest slugs look like `manifest-<theme>-<idx>`. Return `[theme, idx]`
  // or `null` when the slug doesn't match.
  const _parseManifestSlug = (slug) => {
    const m = /^manifest-([a-z0-9-]+?)-(\d+)$/i.exec(slug || '');
    return m ? [m[1], m[2]] : null;
  };

  // Card *body* tap → navigate to the content page. Never plays audio.
  const openItem = (item) => {
    if (!item) return;
    if (isLocked(item)) {
      trackEvent('recommendation_open', { contentId: item.id, sourceContext: 'today_paywall' });
      setPaywall({ open: true, title: item.title });
      return;
    }
    trackEvent('recommendation_open', {
      contentId: item.id,
      sourceContext: 'today',
      reasonKey: item.reasonKey || null,
    });
    if (item.type === 'video') { navigate('/videos'); return; }
    // rowdy_message → short-form video, open inline modal
    if (item.type === 'rowdy_message') {
      const hasVideo = item.videoUrl || item.videoAssetId;
      if (hasVideo) { setVideoModal({ open: true, item }); return; }
      navigate('/videos');
      return;
    }
    if (item.type === 'affirmation') {
      const parsed = _parseManifestSlug(item.slug);
      if (parsed) { navigate(`/a/${parsed[0]}/${parseInt(parsed[1], 10)}`); return; }
      navigate('/manifest'); return;
    }
    navigate(`/post/${item.slug || item.id}`);
  };

  // Gold play badge → play inline. Uses a real recording when the item has
  // one; falls back to Rowdy's Resemble voice for affirmations without audio.
  const playItem = async (item) => {
    if (!item) return;
    if (isLocked(item)) {
      trackEvent('recommendation_open', { contentId: item.id, sourceContext: 'today_paywall' });
      setPaywall({ open: true, title: item.title });
      return;
    }
    trackEvent('recommendation_play', {
      contentId: item.id,
      sourceContext: 'today',
      reasonKey: item.reasonKey || null,
    });
    if (item.type === 'video') { navigate('/videos'); return; }

    // Rowdy Message clips are short videos — open the inline video modal so
    // the user stays on Today. Uses the original videoUrl / videoAssetId
    // on the record. Never falls back to TTS for these; they play from
    // Rowdy's own original file.
    if (item.type === 'rowdy_message') {
      const hasVideo = item.videoUrl || item.videoAssetId;
      if (hasVideo) {
        setVideoModal({ open: true, item });
        return;
      }
      // No video attached — safe fallback to the videos page.
      navigate('/videos');
      return;
    }

    const src = item.audioUrl || (item.audioAssetId ? `${API_URL}/api/media/${item.audioAssetId}` : null);
    if (src) {
      play({
        src,
        title: item.title,
        subtitle: item.creator || 'Rowdy Solomon',
        artwork: item.coverImageUrl,
        kind: item.type === 'soundscape' ? 'soundscape' : 'audio',
        contentId: item.id,
        sourceContext: 'today',
        loop: item.type === 'soundscape',
      });
      return;
    }

    // No recording — stream Rowdy's TTS voice ONLY for affirmations.
    if (item.type === 'affirmation') {
      const parsed = _parseManifestSlug(item.slug);
      // The TTS route expects `{theme}/{index}.mp3` path params — only used
      // for logging + URL uniqueness (cache is content-addressed by text
      // hash).
      const theme = parsed ? parsed[0] : 'today';
      const index = parsed ? parsed[1] : String(Math.abs(hashCode(item.slug || item.id || 'x')) % 100).padStart(2, '0');
      // Ask the server for the MP3. Response is streamed audio — feed the
      // request URL to <audio> via the AudioContext with a Bearer header.
      // AudioContext only accepts a `src` URL, so we fetch the MP3 blob
      // client-side (auth-gated) and pass an object-URL.
      const token = localStorage.getItem('rbr_token');
      if (!token) {
        // Rowdy's voice stays members-only (paid voice minutes) — visitors read the affirmation instead.
        toast('Sign in to hear this in Rowdy\'s voice', { description: 'Reading it is free for everyone — opening the affirmation.' });
        openItem(item);
        return;
      }
      // Prefer body text → shortDescription → title, whichever is longest.
      const candidates = [item.description, item.shortDescription, item.title]
        .map((s) => (typeof s === 'string' ? s.trim() : ''))
        .filter(Boolean);
      const spoken = candidates.sort((a, b) => b.length - a.length)[0] || '';
      try {
        const r = await fetch(`${API_URL}/api/tts/manifest/${theme}/${index}.mp3`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text: spoken }),
        });
        if (!r.ok) {
          if (r.status === 429) toastVoiceThrottled();
          else if (r.status === 402) toastVoiceCredits();
          else if (r.status === 503) toastVoiceUnavailable();
          else toastVoiceUnavailable();
          return;
        }
        const blob = await r.blob();
        const src2 = URL.createObjectURL(blob);
        play({
          src: src2,
          title: item.title,
          subtitle: 'Rowdy Solomon',
          artwork: item.coverImageUrl,
          kind: 'audio',
          contentId: item.id,
          sourceContext: 'today',
        });
      } catch {
        toastVoiceUnavailable();
      }
      return;
    }

    // No audio for a non-affirmation content type — fall back to open.
    openItem(item);
  };

  // Deprecated alias kept for the FeatureCard callers below that haven't
  // been split yet; behaves like the old auto-play-then-navigate.
  const routeTo = (item) => {
    const hasAudio = item?.audioUrl || item?.audioAssetId;
    if (hasAudio || item?.type === 'affirmation') return playItem(item);
    return openItem(item);
  };

  if (loading && !data) {
    return (
      <div className="px-4 mt-4" data-testid="today-personalized-skeleton">
        <div className="h-24 rounded-2xl bg-stone-200/50 animate-pulse" />
        <div className="h-40 mt-3 rounded-3xl bg-stone-200/50 animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const {
    greeting, firstName, daypart,
    continueListening, dailyAffirmation,
    momentWithRowdy, sixtySecondsWithRowdy, meditationsShelf, sleepRecommendation,
    recommendedForYou, activeProgram, journalPrompt,
  } = data;

  return (
    <div className="px-4" data-testid="today-personalized">
      {/* Greeting */}
      <div className="mt-2 mb-4" data-testid="today-greeting">
        <p className="rbr-eyebrow" style={{ color: '#C79A4A' }}>
          Reflections by Rowdy
        </p>
        <h1
          className="text-3xl leading-tight mt-1"
          style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif", color: '#F8F6F0' }}
        >
          {firstName ? `${greeting}, ${firstName}.` : `${greeting}.`}
        </h1>
        <p className="text-sm mt-1" style={{ color: '#A3BEAF' }}>Take what you need today.</p>
      </div>

      {/* A Moment With Rowdy — sits above the Reflection Coach on user's request. */}
      <div className="-mt-2">
        <FeatureCard
          item={momentWithRowdy}
          onPick={openItem}
          onPlay={playItem}
          eyebrow="A moment with Rowdy"
          testid="today-moment-with-rowdy"
          tone="gold"
        />
      </div>

      {coachSlot && <div className="mt-4 mb-4" data-testid="today-coach-slot">{coachSlot}</div>}

      {/* Mood check-in — refresh Today on submit so recommendations update immediately */}
      <div className="mb-4">
        <MoodCheckInCard onCheckIn={() => refresh()} />
      </div>

      {/* Continue Listening */}
      <Rail
        title="Continue Listening"
        subtitle="Pick up where you left off"
        items={continueListening}
        onPick={openItem}
        onPlay={playItem}
        testid="today-continue-listening"
      />

      {/* 60 Seconds With Rowdy — short-form videos; tap the play button
          to open the post page which plays the original Rowdy video. */}
      <Rail
        title="60 Seconds With Rowdy"
        subtitle="Small moments, big presence"
        items={sixtySecondsWithRowdy}
        onPick={openItem}
        onPlay={playItem}
        testid="today-sixty-seconds"
      />

      {/* Meditations */}
      <Rail
        title="Meditations"
        subtitle="Slow down and settle in"
        items={meditationsShelf}
        onPick={openItem}
        onPlay={playItem}
        testid="today-meditations-shelf"
        anchorId="today-meditations-shelf"
      />

      {/* Recommended For You */}
      <Rail
        title="Recommended for you"
        subtitle="Personalized for where you are"
        items={recommendedForYou}
        onPick={openItem}
        onPlay={playItem}
        testid="today-recommended"
      />

      {/* --- Contextual / conditional cards slot BELOW the primary rails --- */}

      {/* "For This Moment" card removed on user's request. */}

      {/* A Moment With Rowdy now renders near the top (above the Reflection Coach). */}

      {/* Sleep recommendation — night only */}
      {daypart === 'night' && sleepRecommendation && (
        <FeatureCard
          item={sleepRecommendation}
          onPick={openItem}
          onPlay={playItem}
          eyebrow="Let the day end here"
          testid="today-sleep-recommendation"
          tone="night"
        />
      )}

      {/* Today's Affirmation section removed on user's request. */}

      {/* Daily Reflection card removed on user's request. */}

      {/* Active Program */}
      {activeProgram && (
        <Link
          to={`/programs/${activeProgram.programId}`}
          className="mt-6 w-full text-left rounded-2xl p-4 bg-[#173F35] flex items-center gap-4 active:scale-[0.99] transition-transform"
          data-testid="today-active-program"
          style={{ color: '#F6F1E7' }}
        >
          <Compass className="w-10 h-10 flex-shrink-0" style={{ color: '#C79A4A' }} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#C79A4A' }}>
              Continue your journey
            </p>
            <p className="text-lg leading-tight mt-0.5 truncate" style={{ fontFamily: "'DM Serif Display', serif" }}>
              {activeProgram.title || 'Your program'}
            </p>
            {activeProgram.currentDay && activeProgram.totalDays && (
              <p className="text-[11px] mt-0.5" style={{ color: 'rgba(246,241,231,0.7)' }}>
                Day {activeProgram.currentDay} of {activeProgram.totalDays}
              </p>
            )}
          </div>
          <ArrowRight className="w-5 h-5" />
        </Link>
      )}

      {/* Journal "Take this with you" prompt removed on user's request. */}

      {/* Contextual paywall — only shown when a free user intentionally taps
          a Premium item on Today. Never appears from a mood check-in. */}
      <PaywallModal
        open={paywall.open}
        contentTitle={paywall.title}
        onClose={() => setPaywall({ open: false, title: null })}
      />

      {/* Inline video player for 60 Seconds With Rowdy clips — auto-advances
          through the rail so users can "watch them all" without tapping. */}
      <VideoModal
        open={videoModal.open}
        item={videoModal.item}
        onClose={() => setVideoModal({ open: false, item: null })}
        onEnded={(curItem) => {
          // Return the next item in the sixtySecondsWithRowdy rail (looping
          // back to the first). Returning null / undefined closes the modal.
          const list = Array.isArray(sixtySecondsWithRowdy) ? sixtySecondsWithRowdy : [];
          if (list.length < 2) return null;
          const i = list.findIndex((c) => c.id === curItem?.id);
          if (i < 0) return null;
          const next = list[(i + 1) % list.length];
          const hasVideo = next?.videoUrl || next?.videoAssetId;
          return hasVideo ? next : null;
        }}
        onNext={(nextItem) => setVideoModal({ open: true, item: nextItem })}
      />
    </div>
  );
};

export default TodayPersonalized;
